// react
import { useContext, useEffect, useMemo, useState } from 'react';

// router
import { useNavigate, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { WorkPlan } from '../../../../@types';

// context
import { ContextBanner } from '../../../../App.context';
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// mui
import {
	alpha,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Divider,
	Grid,
	IconButton,
	LinearProgress,
	Menu,
	MenuItem,
	Paper,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';

// icons
import {
	Add as IconAdd,
	CalendarMonth as IconCalendar,
	CheckCircle as IconCheck,
	Download as IconDownload,
	Assignment as IconWorkPlan,
	MoreVert as IconMore,
	RadioButtonUnchecked as IconUnchecked,
	UploadFile as IconUpload,
} from '@mui/icons-material';

// components
import { Empty } from '../../../ui/Empty/Empty';
import { CustomFieldsLink } from '../../../ui/CustomFieldsLink/CustomFieldsLink';
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';
import { TruncatedText } from '../../../ui/TruncatedText/TruncatedText';
import { PaginationBar, SearchFilterBar } from '../../../ui/SearchFilterBar/SearchFilterBar';

// ui
import { BtnSplit } from '../../../ui/BtnSplit/BtnSplit';

// dialogs
import {
	DialogBulkUpload,
	v2WorkPlanSchema,
	downloadTemplate,
	schemaWithCustomFields,
	LookupData,
} from '../../../dialogs/DialogBulkUpload';
import { DialogActions } from '../../../dialogs/DialogActions/DialogActions';

// hooks
import { useProjectLookups } from '../../../../hooks/useProjectLookups';

// data
import { COLORS, GRADIENTS } from '../../../../data';

// design system
import { radius, spacing, typography } from '../../../../utils/designSystem';

// utils
import { formatCurrency } from '../../../../utils/currency';

export const WorkPlans = () => {
	// hooks
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const {
		conditions,
		deactivateWorkPlan,
		deleteWorkPlan,
		workPlans,
		getActivitiesByWorkPlan,
		loading,
		pagination,
		refetchWorkPlans,
		setFilterQuery,
	} = useContext(ContextSpendPlan);

	// params
	const paramDialog = searchParams.get('dialog');
	const filterPage = searchParams.get('page') || '1';
	const filterLimit = searchParams.get('limit') || '12';
	const searchQuery = searchParams.get('search') || '';

	// Sync URL params with context filter query
	useEffect(() => {
		setFilterQuery('workPlans', {
			...(searchQuery && { search: searchQuery }),
			page: parseInt(filterPage),
			limit: parseInt(filterLimit),
		});
	}, [filterPage, filterLimit, searchQuery, setFilterQuery]);

	// state
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [menuWorkPlan, setMenuWorkPlan] = useState<WorkPlan | null>(null);
	const [workPlanPendingHardDelete, setWorkPlanPendingHardDelete] = useState<WorkPlan | null>(null);
	const [isHardDeletingWorkPlan, setIsHardDeletingWorkPlan] = useState(false);

	// Lookup data for bulk upload. Fetched as the full project universe (no page
	// limit) so the resolve-relationship dropdowns list every implementing entity
	// and every work plan for the project, not just the current listing page.
	const lookups = useProjectLookups({
		projectId: project?.id,
		isEnabled: paramDialog === 'bulkUpload',
		entityTypes: 'PIU,SUB_PIU',
		workPlans: true,
	});
	const lookupData: LookupData = useMemo(
		() => ({
			entities: lookups.entities,
			workPlans: lookups.workPlans,
		}),
		[lookups.entities, lookups.workPlans]
	);

	// handlers for filters
	const handleSearchChange = (value: string) => {
		setSearchParams((params) => {
			if (value) {
				params.set('search', value);
			} else {
				params.delete('search');
			}
			return params;
		});
	};

	// handlers
	const handleCloseDialog = () => {
		setSearchParams((params) => {
			params.delete('dialog');
			params.delete('dialogWorkPlan');
			return params;
		});
	};

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, workPlan: WorkPlan) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
		setMenuWorkPlan(workPlan);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setMenuWorkPlan(null);
	};

	const handleWorkPlanClick = (workPlan: WorkPlan) => {
		navigate(`/project/${project?.idWbg}/spend-plans/work-planning/work-plan/${workPlan.id}`);
	};

	const handleOpenCreateDialog = () => {
		searchParams.set('dialog', 'createWorkPlan');
		setSearchParams(searchParams);
	};

	const handleBulkUploadFile = () => {
		searchParams.set('dialog', 'bulkUpload');
		setSearchParams(searchParams);
	};

	const handleDownloadTemplate = async () => {
		try {
			await downloadTemplate({
				schema: schemaWithCustomFields(v2WorkPlanSchema, project?.customFieldDefinitions ?? []),
				lookupData,
			});
			setBanner({ message: t('done'), severity: 'success' });
		} catch {
			setBanner({ message: t('spendPlan.workPlan.failedToGenerateTemplate'), severity: 'error' });
		}
	};

	const handleOpenEditDialog = () => {
		if (!menuWorkPlan) return;
		searchParams.set('dialog', 'editWorkPlan');
		searchParams.set('dialogWorkPlan', String(menuWorkPlan.id));
		setSearchParams(searchParams);
		handleMenuClose();
	};

	const handleDeactivateWorkPlan = async () => {
		if (!menuWorkPlan) return;

		const response = await deactivateWorkPlan(menuWorkPlan.id);

		if (response.status === 200 || response.status === 204 || !response.error) {
			setBanner({
				message: t('spendPlan.workPlanDeactivated', { name: menuWorkPlan.name }),
				severity: 'success',
			});
		} else if (response.error) {
			setBanner({
				message: t('spendPlan.workPlan.failedToDeactivate', { message: response.error.message }),
				severity: 'error',
			});
		}

		handleMenuClose();
	};

	const handleDeleteDraftWorkPlan = () => {
		if (!menuWorkPlan) return;
		// Stash before menu close nulls menuWorkPlan; dialog still needs it.
		setWorkPlanPendingHardDelete(menuWorkPlan);
		handleMenuClose();
	};

	const handleConfirmHardDeleteWorkPlan = async () => {
		if (!workPlanPendingHardDelete) return;
		setIsHardDeletingWorkPlan(true);
		const response = await deleteWorkPlan(workPlanPendingHardDelete.id);
		setIsHardDeletingWorkPlan(false);

		if (!response.error) {
			setBanner({
				message: t('spendPlan.workPlanDeactivated', { name: workPlanPendingHardDelete.name }),
				severity: 'success',
			});
		} else {
			const hint = (response.error as { details?: { hint?: string } }).details?.hint;
			setBanner({
				message: t('spendPlan.workPlan.failedToDelete', { message: `${response.error.message}${hint ? ` ${hint}` : ''}` }),
				severity: 'error',
			});
		}

		setWorkPlanPendingHardDelete(null);
	};

	return (
		<Box>
			{/* Header - Full width, no padding */}
			<HeaderListingPage
				icon={IconWorkPlan}
				title={t('spendPlan.workPlanning')}
				subtitle={t('spendPlan.workPlanDescription')}
				variant="workPlan"
				secondaryBadge={<CustomFieldsLink entity="workPlan" />}
				actions={
					<BtnSplit
						actions={[
							{
								children: t('spendPlan.createWorkPlan'),
								onClick: handleOpenCreateDialog,
								startIcon: <IconAdd />,
							},
							{
								children: t('spendPlan.bulkUploadFromFile'),
								onClick: handleBulkUploadFile,
								startIcon: <IconUpload />,
							},
							{
								children: t('spendPlan.downloadTemplate'),
								onClick: handleDownloadTemplate,
								startIcon: <IconDownload />,
							},
						]}
					/>
				}
			/>

			{/* Content with padding */}
			<Box sx={{ pb: 3, px: 3, pt: 2 }}>
				{/* Search & Filters */}
				<Box sx={{ mb: spacing.md }}>
					<SearchFilterBar
						searchPlaceholder={t('spendPlan.searchWorkPlans')}
						searchValue={searchQuery}
						onSearchChange={handleSearchChange}
						filters={[]}
						filterValues={{}}
						// eslint-disable-next-line @typescript-eslint/no-empty-function
						onFilterChange={() => {}}
					/>
				</Box>

				{/* Work Plan Grid */}
				{!loading.workPlans && workPlans.length > 0 && (
					<Grid container spacing={spacing.lg}>
						{workPlans.map((workPlan) => {
							// Get related data
							const activities = getActivitiesByWorkPlan(workPlan.id);
							const workPlanConditions = conditions.filter((c) => {
								const condWorkPlan = typeof c.workPlan === 'number' ? c.workPlan : c.workPlan?.id;
								return condWorkPlan === workPlan.id;
							});

							// Calculate utilization (using activities amounts as committed)
							const committedAmount = activities.reduce((sum, a) => sum + (a.amount || 0), 0);
							const utilization =
								workPlan.amount > 0 ? Math.round((committedAmount / workPlan.amount) * 100) : 0;

							// Local-currency equivalents (only available when workPlan has a local currency)
							const hasLocal =
								!!workPlan.currencyLocal &&
								workPlan.amountLocal != null &&
								workPlan.amountLocal > 0;
							const localRate =
								hasLocal && workPlan.amount > 0
									? (workPlan.amountLocal as number) / workPlan.amount
									: 0;
							const committedAmountLocal = hasLocal
								? activities.reduce(
										(sum, a) =>
											sum +
											(a.currencyLocal === workPlan.currencyLocal && a.amountLocal != null
												? a.amountLocal
												: (a.amount || 0) * localRate),
										0
									)
								: 0;
							const availableAmountLocal = hasLocal
								? Math.max(0, (workPlan.amountLocal as number) - committedAmountLocal)
								: 0;
							const metConditions = workPlanConditions.filter(
								(c) => c.status === 'VERIFIED'
							).length;
							const totalConditions = workPlanConditions.length;

							// Get populated values or fallbacks
							const entityName = typeof workPlan.entity === 'object' ? workPlan.entity?.name : null;

							return (
								<Grid item xs={12} md={6} lg={4} key={workPlan.id}>
									<Card
										elevation={0}
										onClick={() => handleWorkPlanClick(workPlan)}
										sx={{
											border: '1px solid',
											borderColor: alpha(GRADIENTS.workPlan.border, 0.3),
											borderRadius: 2,
											cursor: 'pointer',
											display: 'flex',
											flexDirection: 'column',
											height: '100%',
											overflow: 'hidden',
											transition: 'all 0.2s ease-in-out',
											'&:hover': {
												borderColor: alpha(GRADIENTS.workPlan.border, 0.5),
												boxShadow: `0 4px 12px ${alpha(GRADIENTS.workPlan.icon, 0.15)}`,
												transform: 'translateY(-2px)',
											},
										}}>
										{/* Header */}
										<Box
											sx={{
												background: GRADIENTS.workPlan.bg,
												px: 2,
												py: 1.25,
											}}>
											<Stack direction="row" justifyContent="space-between" alignItems="flex-start">
												<Stack
													direction="row"
													spacing={1.5}
													alignItems="center"
													sx={{ minWidth: 0, flex: 1 }}>
													<Box
														sx={{
															alignItems: 'center',
															bgcolor: alpha(GRADIENTS.workPlan.icon, 0.12),
															borderRadius: 1.5,
															display: 'flex',
															flexShrink: 0,
															height: 36,
															justifyContent: 'center',
															width: 36,
														}}>
														<IconWorkPlan sx={{ color: GRADIENTS.workPlan.icon, fontSize: 20 }} />
													</Box>
													<Box sx={{ minWidth: 0, flex: 1 }}>
														<TruncatedText
															variant="subtitle1"
															text={workPlan.name}
															sx={{
																color: GRADIENTS.workPlan.text,
																fontWeight: 700,
																lineHeight: 1.3,
															}}
														/>
														<TruncatedText
															text={entityName || workPlan.fiscalYear || ''}
															sx={{
																...typography.body,
																fontSize: '1.25rem',
																color: GRADIENTS.workPlan.subtitle,
															}}
														/>
													</Box>
												</Stack>
												<IconButton
													size="small"
													sx={{
														color: GRADIENTS.workPlan.subtitle,
														'&:hover': { bgcolor: alpha(GRADIENTS.workPlan.icon, 0.1) },
													}}
													onClick={(e) => handleMenuOpen(e, workPlan)}>
													<IconMore />
												</IconButton>
											</Stack>
										</Box>

										<CardContent sx={{ display: 'flex', flex: 1, flexDirection: 'column', p: 0 }}>
											{/* Planning Period + Status Row */}
											<Box sx={{ bgcolor: 'grey.50', px: spacing.sm, py: spacing.xs }}>
												<Stack
													direction="row"
													spacing={1}
													alignItems="center"
													flexWrap="wrap"
													useFlexGap>
													{workPlan.dateStart && workPlan.dateEnd && (
														<Chip
															icon={<IconCalendar sx={{ fontSize: 20 }} />}
															label={`${new Date(workPlan.dateStart).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })} - ${new Date(workPlan.dateEnd).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })}`}
															size="medium"
															variant="outlined"
															sx={{ fontWeight: 500, fontSize: '1.35rem' }}
														/>
													)}
													<Chip
														label={workPlan.status}
														size="medium"
														color={
															workPlan.status === 'ACTIVE' || workPlan.status === 'COMPLETED'
																? 'success'
																: workPlan.status === 'DRAFT'
																	? 'warning'
																	: workPlan.status === 'SUBMITTED'
																		? 'info'
																		: 'default'
														}
														sx={{ fontWeight: 600, fontSize: '1.35rem' }}
													/>
												</Stack>
											</Box>

											<Divider />

											{/* Amounts Grid */}
											<Box sx={{ px: spacing.sm, py: spacing.sm }}>
												<Grid container spacing={1.5}>
													<Grid item xs={6}>
														<Typography
															sx={{
																fontSize: '1.35rem',
																fontWeight: 600,
																letterSpacing: '0.02em',
																textTransform: 'uppercase',
																color: 'text.secondary',
																display: 'block',
																mb: 0.25,
															}}>
															{t('spendPlan.workPlanAmount')}
														</Typography>
														<Tooltip title={t('spendPlan.workPlanAmountTooltip', { currency: workPlan.currency })} arrow>
															<Typography
																sx={{
																	fontWeight: 600,
																	fontSize: '1.5rem',
																	lineHeight: 1.2,
																	display: 'inline-block',
																}}>
																{formatCurrency({
																	currency: workPlan.currency,
																	value: workPlan.amount,
																})}
															</Typography>
														</Tooltip>
														{hasLocal && (
															<Tooltip
																title={t('spendPlan.workPlanAmountLocalTooltip', { currency: workPlan.currencyLocal })}
																arrow>
																<Typography
																	sx={{
																		fontWeight: 500,
																		fontSize: '1.1rem',
																		lineHeight: 1.2,
																		color: 'text.secondary',
																		mt: 0.25,
																		display: 'block',
																	}}>
																	{formatCurrency({
																		currency: workPlan.currencyLocal as string,
																		value: workPlan.amountLocal as number,
																	})}
																</Typography>
															</Tooltip>
														)}
													</Grid>
													<Grid item xs={6}>
														<Typography
															sx={{
																fontSize: '1.35rem',
																fontWeight: 600,
																letterSpacing: '0.02em',
																textTransform: 'uppercase',
																color: COLORS.warning.main,
																display: 'block',
																mb: 0.25,
															}}>
															{t('planned')}
														</Typography>
														<Tooltip title={t('spendPlan.workPlanAmountTooltip', { currency: workPlan.currency })} arrow>
															<Typography
																sx={{
																	fontWeight: 600,
																	fontSize: '1.5rem',
																	color: COLORS.warning.dark,
																	lineHeight: 1.2,
																	display: 'inline-block',
																}}>
																{formatCurrency({
																	currency: workPlan.currency,
																	value: committedAmount,
																})}
															</Typography>
														</Tooltip>
														{hasLocal && (
															<Tooltip
																title={t('spendPlan.workPlanAmountLocalTooltip', { currency: workPlan.currencyLocal })}
																arrow>
																<Typography
																	sx={{
																		fontWeight: 500,
																		fontSize: '1.1rem',
																		lineHeight: 1.2,
																		color: COLORS.warning.dark,
																		opacity: 0.75,
																		mt: 0.25,
																		display: 'block',
																	}}>
																	{formatCurrency({
																		currency: workPlan.currencyLocal as string,
																		value: committedAmountLocal,
																	})}
																</Typography>
															</Tooltip>
														)}
													</Grid>
													<Grid item xs={6}>
														<Typography
															sx={{
																fontSize: '1.35rem',
																fontWeight: 600,
																letterSpacing: '0.02em',
																textTransform: 'uppercase',
																color: COLORS.info.main,
																display: 'block',
																mb: 0.25,
															}}>
															{t('spendPlan.availableToPlan')}
														</Typography>
														<Tooltip title={t('spendPlan.availableToPlanTooltip', { currency: workPlan.currency })} arrow>
															<Typography
																sx={{
																	fontWeight: 600,
																	fontSize: '1.5rem',
																	color: COLORS.info.dark,
																	lineHeight: 1.2,
																	display: 'inline-block',
																}}>
																{formatCurrency({
																	currency: workPlan.currency,
																	value: Math.max(0, workPlan.amount - committedAmount),
																})}
															</Typography>
														</Tooltip>
														{hasLocal && (
															<Tooltip
																title={t('spendPlan.availableToPlanLocalTooltip', { currency: workPlan.currencyLocal })}
																arrow>
																<Typography
																	sx={{
																		fontWeight: 500,
																		fontSize: '1.1rem',
																		lineHeight: 1.2,
																		color: COLORS.info.dark,
																		opacity: 0.75,
																		mt: 0.25,
																		display: 'block',
																	}}>
																	{formatCurrency({
																		currency: workPlan.currencyLocal as string,
																		value: availableAmountLocal,
																	})}
																</Typography>
															</Tooltip>
														)}
													</Grid>
													<Grid item xs={6}>
														<Typography
															sx={{
																fontSize: '1.35rem',
																fontWeight: 600,
																letterSpacing: '0.02em',
																textTransform: 'uppercase',
																color: 'text.secondary',
																display: 'block',
																mb: 0.25,
															}}>
															{t('activities')}
														</Typography>
														<Typography
															sx={{ fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.2 }}>
															{activities.length}
														</Typography>
													</Grid>
												</Grid>
											</Box>

											<Divider />
											{/* Utilization Bar */}
											<Box sx={{ px: spacing.sm, py: spacing.xs }}>
												<Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
													<Typography sx={{ fontSize: '1.35rem', fontWeight: 600 }}>
														{t('utilization')}
													</Typography>
													<Typography
														sx={{
															fontSize: '1.35rem',
															color:
																utilization > 90
																	? 'error.main'
																	: utilization > 70
																		? COLORS.warning.main
																		: COLORS.success.main,
															fontWeight: 700,
														}}>
														{utilization}%
													</Typography>
												</Stack>
												<LinearProgress
													variant="determinate"
													value={Math.min(utilization, 100)}
													sx={{
														bgcolor: 'grey.200',
														borderRadius: radius.sm,
														height: 6,
														'& .MuiLinearProgress-bar': {
															bgcolor:
																utilization > 90
																	? 'error.main'
																	: utilization > 70
																		? COLORS.warning.main
																		: COLORS.success.main,
															borderRadius: radius.sm,
														},
													}}
												/>
											</Box>

											{/* Conditions */}
											{totalConditions > 0 && (
												<>
													<Divider />
													<Box sx={{ flex: 1, px: spacing.sm, py: spacing.xs }}>
														<Typography
															sx={{ fontSize: '1.5rem', fontWeight: 600, mb: spacing.xs }}>
															{t('spendPlan.workPlanConditions', { met: metConditions, total: totalConditions })}
														</Typography>
														<Stack spacing={0.5}>
															{workPlanConditions.slice(0, 3).map((condition) => (
																<Stack
																	key={condition.id}
																	direction="row"
																	alignItems="center"
																	spacing={1}
																	sx={{ minWidth: 0 }}>
																	{condition.status === 'VERIFIED' ? (
																		<IconCheck
																			sx={{
																				color: COLORS.success.main,
																				fontSize: 22,
																				flexShrink: 0,
																			}}
																		/>
																	) : (
																		<IconUnchecked
																			sx={{ color: 'grey.400', fontSize: 22, flexShrink: 0 }}
																		/>
																	)}
																	<TruncatedText
																		text={condition.name}
																		sx={{
																			fontSize: '1.35rem',
																			color:
																				condition.status === 'VERIFIED'
																					? 'text.primary'
																					: 'text.secondary',
																			fontWeight: condition.status === 'VERIFIED' ? 500 : 400,
																		}}
																	/>
																</Stack>
															))}
															{workPlanConditions.length > 3 && (
																<Typography
																	sx={{
																		fontSize: '1.35rem',
																		color: 'text.secondary',
																	}}>
																	{t('spendPlan.workPlanMoreConditions', { count: workPlanConditions.length - 3 })}
																</Typography>
															)}
														</Stack>
													</Box>
												</>
											)}

											{/* Footer */}
											<Box
												sx={{
													bgcolor: 'grey.50',
													borderRadius: `0 0 ${radius.lg * 8}px 0`,
													mt: 'auto',
													px: spacing.sm,
													py: spacing.xs,
												}}>
												<Stack direction="row" justifyContent="flex-end" alignItems="center">
													<Button
														onClick={(e) => {
															e.stopPropagation();
															handleWorkPlanClick(workPlan);
														}}
														size="medium"
														sx={{
															fontSize: '1.5rem',
															fontWeight: 600,
															minWidth: 'auto',
															px: 2,
															textTransform: 'none',
														}}>
														{t('spendPlan.viewDetails')}
													</Button>
												</Stack>
											</Box>
										</CardContent>
									</Card>
								</Grid>
							);
						})}
					</Grid>
				)}

				{/* Empty/Loading state */}
				{(workPlans.length === 0 || loading.workPlans) && (
					<Paper sx={{ mt: 2, p: 6 }}>
						<Empty
							isLoading={loading.workPlans}
							title={{
								children: searchQuery ? t('empty.default.title') : t('spendPlan.noWorkPlansFound'),
							}}
							content={{
								children: searchQuery
									? t('spendPlan.tryAdjustingFilters')
									: t('empty.default.content'),
							}}
							actions={[
								{
									children: t('spendPlan.createWorkPlan'),
									onClick: handleOpenCreateDialog,
									variant: 'contained',
								},
							]}
						/>
					</Paper>
				)}

				{/* Pagination */}
				<PaginationBar totalCount={pagination.workPlans?.total || workPlans.length} />
			</Box>

			{/* Menu */}
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleMenuClose}
				anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
				transformOrigin={{ horizontal: 'right', vertical: 'top' }}>
				<MenuItem onClick={handleOpenEditDialog}>{t('spendPlan.editWorkPlan')}</MenuItem>
				<MenuItem onClick={handleMenuClose}>{t('spendPlan.viewActivities')}</MenuItem>
				<MenuItem onClick={handleMenuClose}>{t('spendPlan.addCondition')}</MenuItem>
				<Divider />
				<MenuItem onClick={handleDeactivateWorkPlan} sx={{ color: 'error.main' }}>
					{t('spendPlan.deactivateWorkPlan')}
				</MenuItem>
				{menuWorkPlan?.status === 'DRAFT' && (
					<MenuItem onClick={handleDeleteDraftWorkPlan} sx={{ color: 'error.main' }}>
						Delete Draft
					</MenuItem>
				)}
			</Menu>

			{/* Bulk Upload Work Plans Dialog */}
			<DialogBulkUpload
				isOpen={paramDialog === 'bulkUpload'}
				onClose={handleCloseDialog}
				schema={v2WorkPlanSchema}
				projectId={project?.id || 0}
				lookupData={lookupData}
				customFieldDefinitions={project?.customFieldDefinitions}
				onSuccess={() => {
					refetchWorkPlans();
					setBanner({ message: t('spendPlan.workPlan.uploadedSuccess'), severity: 'success' });
				}}
			/>

			{/* Delete Draft Work Plan Confirmation */}
			<DialogActions
				isOpen={workPlanPendingHardDelete !== null}
				onClose={() => !isHardDeletingWorkPlan && setWorkPlanPendingHardDelete(null)}
				maxWidth={520}
				title={{ children: 'Delete Draft Work Plan' }}
				subtitle={{
					children: `Permanently delete "${workPlanPendingHardDelete?.name ?? ''}"?`,
				}}
				actions={[
					{
						children: 'Cancel',
						color: 'inherit',
						disabled: isHardDeletingWorkPlan,
						onClick: () => setWorkPlanPendingHardDelete(null),
						variant: 'text',
					},
					{
						children: isHardDeletingWorkPlan ? t('loading') : t('spendPlan.deleteSpendPlan'),
						color: 'error',
						disabled: isHardDeletingWorkPlan,
						loading: isHardDeletingWorkPlan,
						onClick: handleConfirmHardDeleteWorkPlan,
						variant: 'contained',
					},
				]}>
				<Typography sx={{ mt: 1, color: '#374151', fontSize: '1.35rem' }}>
					{t('spendPlan.workPlanDeletionWarning')}
				</Typography>
			</DialogActions>
		</Box>
	);
};
